import { fetchApi } from './api';

export interface Training {
    Id: number;
    UserEmail: string;
    CourseTopic: string;
    Category: string;
    InternalExternal: string;
    StartDate: string;
    EndDate: string;
    DurationHours: number;
    CostsOriginal: number | null;
    CostsCurrency: string | null;
    CostsEur: number | null;
    TrainingOrganizer: string | null;
    PlaceOfTraining: string | null;
    IsSupplierTraining: boolean;
    IsWebTraining: boolean;
    SupervisorEmail: string | null;
    CreatedAt: string;
    CreatedBy: string;
    EmployeeName?: string;
    /** Only on the team and all lists, from etl.EmployeeMaster. */
    CompanyCode?: string | null;
}

/** 'all' is served only to the roles HR and Admin; the API answers 403 to anyone else. */
export type TrainingScope = 'own' | 'team' | 'all';

export interface TrainingSummary {
    TrainingCount: number;
    TotalHours: number;
    TotalCostsEur: number;
    UniqueEmployees: number;
    availableYears: number[];
    isManager: boolean;
    /** Role HR or Admin: may open the overview of all employees. */
    canSeeAll: boolean;
    /** Role HR: may edit and delete every training (the server checks it again). */
    canEditAll: boolean;
}

export interface CreateTrainingData {
    courseTopic: string;
    category: string;
    internalExternal: string;
    startDate: string;
    endDate: string;
    durationHours: number;
    costsOriginal: number | null;
    costsCurrency: string | null;
    costsEur: number | null;
    trainingOrganizer: string | null;
    isWebTraining: boolean;
    placeOfTraining?: string | null;
    isSupplierTraining?: boolean;
}

/**
 * Why an active employee is not on the reminder list (E8, the month-independent part): an
 * address outside @dolder.com / @dolderbigler.com, contract type Hourly or Consultant, or an
 * opt-out. `null` = gets the reminder unless they log a training or confirm the month.
 */
export type ReminderExclusion = 'private-address' | 'contract-type' | 'opted-out';

/** Active employees from etl.EmployeeMaster (PeopleDoc), HR and Admin only. */
export interface ReminderRecipient {
    /**
     * Company address. For ExcludedReason 'private-address' only a placeholder such as
     * "(private address 1)": the server never sends a private address. Not a valid address,
     * so the opt-out PUT refuses it.
     */
    Email: string;
    FirstName: string | null;
    LastName: string | null;
    Department: string | null;
    CompanyCode: string | null;
    ContractType: string | null;
    /** false = opted out in TrainingReminderRecipients. */
    IsEnabled: boolean;
    ExcludedReason: ReminderExclusion | null;
}

/** The caller's own state for one month (GET no-training-confirmations). */
export interface NoTrainingStatus {
    /** YYYY-MM */
    month: string;
    /** Non-deleted trainings with StartDate in the month. */
    trainingCount: number;
    /** ISO timestamp (UTC) of the "no training" confirmation, or null. */
    confirmedAt: string | null;
    /** The month is the current or previous Zurich month (E11): the button may be offered. */
    canConfirm: boolean;
}

export interface NoTrainingConfirmation {
    month: string;
    confirmedAt: string;
    /** false = the month was already confirmed; nothing was written. */
    created: boolean;
}

/** training > confirmed > no-response (reminded, no reaction) > not-reminded. */
export type MonthlyStatus = 'training' | 'confirmed' | 'no-response' | 'not-reminded';

export interface MonthlyStatusRow {
    /**
     * Company address. For excludedReason 'private-address' only a placeholder such as
     * "(private address 1)": the server never sends a private address.
     */
    email: string;
    firstName: string | null;
    lastName: string | null;
    companyCode: string | null;
    trainingCount: number;
    confirmedAt: string | null;
    /** Last successful live reminder for the month; test mails do not count. */
    remindedAt: string | null;
    excludedReason: ReminderExclusion | null;
    status: MonthlyStatus;
}

export interface MonthlyStatusResponse {
    month: string;
    /** "October 2026" */
    monthLabel: string;
    /** Reference day for "active": the reminder day, or today while the month runs. */
    activeOn: string;
    total: number;
    counts: Record<MonthlyStatus, number>;
    rows: MonthlyStatusRow[];
}

export interface ReminderPreview {
    month: string;
    monthLabel: string;
    /** Last working day of the month (YYYY-MM-DD): the day the reminder goes out. */
    reminderDay: string;
    /** IsEnabled of the config row; null when there is no row. */
    enabled: boolean | null;
    /** Who would get the mail now. */
    count: number;
    /** Already reminded for the month (E12), not sent again. */
    alreadySent: number;
    recipients: { email: string; firstName: string; status: 'would-send' | 'already-sent' }[];
}

export interface TestReminderResult {
    sentTo: string;
    month: string;
    sent: number;
    failed: number;
    /** Graph error or skip reason when the test mail did not go out. */
    error: string | null;
}

export interface ReminderConfig {
    Id: number;
    IsEnabled: boolean;
    ReminderDayOfMonth: number;
    ReminderText: string;
    AppUrl: string;
    UpdatedAt: string | null;
    UpdatedBy: string | null;
}

export const trainingLogApi = {
    list(year?: number): Promise<Training[]> {
        const params = year ? `?year=${year}` : '';
        return fetchApi<Training[]>(`/training-log${params}`);
    },

    listTeam(year?: number, scope: 'team' | 'all' = 'team'): Promise<Training[]> {
        const params = new URLSearchParams();
        if (year) params.set('year', String(year));
        params.set('scope', scope);
        return fetchApi<Training[]>(`/training-log/team?${params}`);
    },

    getSummary(year?: number, scope: TrainingScope = 'own'): Promise<TrainingSummary> {
        const params = new URLSearchParams();
        if (year) params.set('year', String(year));
        params.set('scope', scope);
        return fetchApi<TrainingSummary>(`/training-log/summary?${params}`);
    },

    create(data: CreateTrainingData): Promise<{ id: number; createdAt: string }> {
        return fetchApi('/training-log', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    update(id: number, data: CreateTrainingData): Promise<{ id: number; updated: boolean }> {
        return fetchApi(`/training-log/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    delete(id: number): Promise<{ id: number; deleted: boolean }> {
        return fetchApi(`/training-log/${id}`, {
            method: 'DELETE',
        });
    },

    getExchangeRate(currency: string): Promise<{ currency: string; exchangeRate: number }> {
        return fetchApi(`/training-log/exchange-rate/${currency}`);
    },

    getReminderConfig(): Promise<ReminderConfig> {
        return fetchApi('/training-log/reminder-config');
    },

    updateReminderConfig(data: Partial<Pick<ReminderConfig, 'IsEnabled' | 'ReminderDayOfMonth' | 'ReminderText' | 'AppUrl'>>): Promise<{ updated: boolean }> {
        return fetchApi('/training-log/reminder-config', {
            method: 'PUT',
            body: JSON.stringify({
                isEnabled: data.IsEnabled,
                reminderDayOfMonth: data.ReminderDayOfMonth,
                reminderText: data.ReminderText,
                appUrl: data.AppUrl,
            }),
        });
    },

    getReminderRecipients(): Promise<ReminderRecipient[]> {
        return fetchApi<ReminderRecipient[]>('/training-log/reminder-recipients');
    },

    updateReminderRecipients(recipients: { email: string; isEnabled: boolean }[]): Promise<{ updated: number }> {
        return fetchApi('/training-log/reminder-recipients', {
            method: 'PUT',
            body: JSON.stringify({ recipients }),
        });
    },

    /** Exactly one test mail, to `to` (default: the caller); company domains only (E13). */
    sendTestReminder(to?: string): Promise<TestReminderResult> {
        return fetchApi('/training-log/reminder-config/test', {
            method: 'POST',
            body: JSON.stringify(to ? { to } : {}),
        });
    },

    /** Dry run: who would get the reminder for `month` (default: current month). Sends nothing. */
    getReminderPreview(month?: string): Promise<ReminderPreview> {
        const params = month ? `?${new URLSearchParams({ month })}` : '';
        return fetchApi<ReminderPreview>(`/training-log/reminder-config/preview${params}`);
    },

    /** The caller's own training count and confirmation for `month` (YYYY-MM). */
    getNoTrainingStatus(month: string): Promise<NoTrainingStatus> {
        return fetchApi<NoTrainingStatus>(`/training-log/no-training-confirmations?${new URLSearchParams({ month })}`);
    },

    /** "That's correct, no training": idempotent, only the current or previous month (E11). */
    confirmNoTraining(month: string): Promise<NoTrainingConfirmation> {
        return fetchApi<NoTrainingConfirmation>('/training-log/no-training-confirmations', {
            method: 'POST',
            body: JSON.stringify({ month }),
        });
    },

    /** HR and Admin: per active employee training / confirmed / no-response / not-reminded. */
    getMonthlyStatus(month: string): Promise<MonthlyStatusResponse> {
        return fetchApi<MonthlyStatusResponse>(`/training-log/monthly-status?${new URLSearchParams({ month })}`);
    },
};
